'use client'

import type { Button } from '@inflow/ui/components/button'
import { Spinner } from '@inflow/ui/components/spinner'
import { cn } from '@inflow/ui/lib/utils'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { type ComponentProps, cloneElement, type ReactElement, useTransition } from 'react'

type ButtonLikeProps = ComponentProps<typeof Button> & { 'data-loading'?: string }

type NavLinkProps = {
	href: string
	children: ReactElement<ButtonLikeProps>
}

export function NavLink({ href, children }: NavLinkProps) {
	const router = useRouter()
	const pathname = usePathname()
	const [isPending, startTransition] = useTransition()
	const isActive = pathname === href

	return (
		<Link
			href={href}
			prefetch
			aria-current={isActive ? 'page' : undefined}
			onClick={(event) => {
				if (isActive) {
					event.preventDefault()
					return
				}
				event.preventDefault()
				startTransition(() => router.push(href))
			}}
			className="inline-flex"
		>
			{cloneElement(children, {
				className: cn('relative inline-flex items-center justify-center', children.props.className),
				disabled: isPending,
				'data-loading': isPending ? '' : undefined,
				children: (
					<>
						<span className={cn(isPending && 'opacity-0')}>{children.props.children}</span>
						{isPending && (
							<span className="absolute inset-0 flex items-center justify-center">
								<Spinner
									className="h-3 w-3"
									aria-hidden
								/>
							</span>
						)}
					</>
				),
			} satisfies Partial<ButtonLikeProps>)}
		</Link>
	)
}
